import { apiClient } from '../../lib/api/client'
import { toSafeImageRef } from '../../lib/thumbnail-image-ref'
import { type AddPartsCandidateRow, toAddPartsCandidateRow } from './rfq-append-lines'

/** Pull the `title` off a Problem+JSON error body — the same mapping
 * `useCatalogSearch.ts`/`useBomDetail.ts` already use. */
function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

/**
 * UID-457 item 2 (ADR-0147) — the compose screen's (`/rfq/new`) "Add parts
 * to this RFQ" modal's real, debounced candidate search (`AddPartsModal.tsx`).
 *
 * Composes the union CLIENT-SIDE at this vertical seam over two authoritative
 * reads — `GET /catalog/search` (tag `Buy`) and `GET /parts?q=` (tag
 * `Library`) — rather than `GET /boms/add-parts/search`. See
 * `docs/adr/0147-uid457-add-parts-candidate-union-at-the-vertical-seam.md`
 * (D1) for the full rationale: that endpoint's catalog half is a hardcoded
 * two-element constant and its shape can't carry the `kind`/`uom` this modal
 * needs, where both `GET /catalog/search` and `GET /parts` already return a
 * full, real `Sourceable` for free.
 *
 * Both reads run in parallel; either failing rejects the whole call rather
 * than silently rendering half a list (ADR-0147 D1). The tag is always the
 * label of the branch that produced the row, never inferred from the
 * `Sourceable` itself. Ordering and dedupe are decided in the ADR: library
 * rows first (the account's own parts are higher-intent on a compose
 * screen), then catalog rows; dedupe by `sourceable.id` ONLY — a library
 * part and a catalog part that happen to share a SKU are different
 * Sourceables with different price provenance and cart eligibility, and
 * both survive.
 */
export async function searchAddPartsCandidates(
  query: string,
): Promise<readonly AddPartsCandidateRow[]> {
  const trimmed = query.trim()
  const q = trimmed.length > 0 ? { q: trimmed } : {}

  const [catalogResult, libraryResult] = await Promise.all([
    apiClient.GET('/catalog/search', { params: { query: { ...q } } }),
    apiClient.GET('/parts', { params: { query: { ...q } } }),
  ])

  if (catalogResult.error || !catalogResult.data) {
    throw new Error(problemTitle(catalogResult.error, 'We couldn’t search the catalog. Try again.'))
  }
  if (libraryResult.error || !libraryResult.data) {
    throw new Error(
      problemTitle(libraryResult.error, 'We couldn’t search your part library. Try again.'),
    )
  }

  // UID-335 follow-up — `GET /parts` resolves a real/generated thumbnail onto
  // `part.thumbnailUrl`, a SIBLING field of `part.sourceable` (never onto
  // `sourceable.imageRef` itself — that only ever carries the catalog
  // service's own `imageRef` passthrough). Without this merge, a custom/
  // uploaded part's thumbnail was resolved server-side but never reached
  // this screen at all: `toReviewLine`/`AddPartsModal` only ever read
  // `sourceable.imageRef`. See `toSafeImageRef`'s doc comment for why the
  // merge needs the root-relative-vs-absolute distinction it applies.
  const libraryRows = libraryResult.data.parts.map((part) => {
    const imageRef = toSafeImageRef(part.thumbnailUrl, part.sourceable.id)
    return toAddPartsCandidateRow(
      imageRef !== undefined ? { ...part.sourceable, imageRef } : part.sourceable,
      'Library',
    )
  })
  // UID-457 follow-up (inline-configure) — `configSchema` rides the
  // `CatalogResultCard` envelope (`item`), never `item.sourceable` itself
  // (see `AddPartsCandidateRow.configSchema`'s doc comment); threaded
  // through here so `AddPartsToRfqModal.tsx` can offer inline configuration
  // for a `kind: 'configurable'` row rather than only a disabled note. A
  // library row (`GET /parts`) never carries a `CatalogResultCard`, so its
  // mapping omits the third argument, same as before.
  const catalogRows = catalogResult.data.items.map((item) =>
    toAddPartsCandidateRow(item.sourceable, 'Buy', item.configSchema),
  )

  const seen = new Set<string>()
  const merged: AddPartsCandidateRow[] = []
  for (const row of [...libraryRows, ...catalogRows]) {
    if (seen.has(row.sourceable.id)) continue
    seen.add(row.sourceable.id)
    merged.push(row)
  }
  return merged
}
